import {ChangeDetectionStrategy, Component, computed, inject, output, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {EMPTY, Subject} from 'rxjs';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';
import {PANEL_DATA} from '../../services/panel.service';
import {ViewSettingsHttpService, ViewSettingsPageQueryModel} from '../../services/view-settings-http.service';
import {ViewSettingsModel} from '../../../models/view-settings.model';

export interface BringozQuickFiltersSelectPanelData {
  selectedId?: string;
  pageName: string;
  placeholder?: string;
}

export interface BringozQuickFilterSelection {
  selectedId?: string;
  selectedItem?: ViewSettingsModel;
}

const LOAD_LIMIT = 20;
const SCROLL_THRESHOLD_PX = 40;

require('./bringoz-quick-filters-select-panel.scss');
@Component({
  standalone: false,
  selector: 'app-bringoz-quick-filters-select-panel',
  templateUrl: './bringoz-quick-filters-select-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BringozQuickFiltersSelectPanelComponent {

  // PanelService closes the panel on this output, so the name is part of its contract.
  readonly onClose = output<BringozQuickFilterSelection>();

  private readonly data = inject<BringozQuickFiltersSelectPanelData>(PANEL_DATA);
  private readonly viewSettingsHttpService = inject(ViewSettingsHttpService);

  readonly selectedId = this.data?.selectedId;
  readonly placeholder = this.data?.placeholder || 'Search Quick Filter';

  readonly queryString = signal('');
  readonly quickFilters = signal<ViewSettingsModel[]>([]);
  readonly isLoading = signal(true);
  readonly isInitialLoading = signal(true);
  readonly isEmpty = computed(() => !this.isLoading() && !this.quickFilters().length);

  private cursor: string = null;
  private readonly reload$ = new Subject<void>();
  private readonly fetchNext$ = new Subject<string>();

  constructor() {
    this.reload$.pipe(
      switchMap(() => this.viewSettingsHttpService.getPage(this.getQueryPayload()).pipe(
        catchError(() => {
          this.setLoaded();
          return EMPTY;
        })
      )),
      takeUntilDestroyed()
    ).subscribe((response) => {
      this.cursor = response.cursor;
      this.quickFilters.set(response.list);
      this.setLoaded();
    });

    this.fetchNext$.pipe(
      switchMap((cursor) => this.viewSettingsHttpService.getPage({...this.getQueryPayload(), cursor}).pipe(
        catchError(() => {
          this.setLoaded();
          return EMPTY;
        }),
        // A new search replaces the list, so a page still in flight must not be appended to it.
        takeUntil(this.reload$)
      )),
      takeUntilDestroyed()
    ).subscribe((response) => {
      this.cursor = response.cursor;
      this.quickFilters.update((quickFilters) => quickFilters.concat(response.list));
      this.setLoaded();
    });

    this.reload();
  }

  onQueryChange(query: string) {
    this.queryString.set(query || '');
    this.reload();
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - SCROLL_THRESHOLD_PX;
    if (isNearBottom && this.cursor && !this.isLoading()) {
      this.isLoading.set(true);
      this.fetchNext$.next(this.cursor);
    }
  }

  onSelectId(quickFilterId: unknown) {
    const quickFilter = this.quickFilters().find((item) => item.id === quickFilterId);
    if (quickFilter) {
      this.onClose.emit({selectedId: quickFilter.id, selectedItem: quickFilter});
    }
  }

  private reload() {
    this.cursor = null;
    this.isLoading.set(true);
    this.isInitialLoading.set(true);
    this.reload$.next();
  }

  private setLoaded() {
    this.isLoading.set(false);
    this.isInitialLoading.set(false);
  }

  private getQueryPayload(): ViewSettingsPageQueryModel {
    return {
      limit: LOAD_LIMIT,
      query: this.queryString(),
      pageName: this.data?.pageName,
      sort: 'name,asc'
    };
  }
}
